import { useCallback, useEffect, useRef, useState } from 'react';
import { Wavoip } from '@wavoip/wavoip-api';
import type { CallActive, Offer } from '@wavoip/wavoip-api';
import type { CallRoutingDecision } from '@supporchat/shared';
import { api } from '../lib/api';
import { notify } from '../lib/notify';

/** O que a interface precisa saber sobre a chamada do momento. */
export interface CallUiState {
  phase: 'idle' | 'incoming' | 'dialing' | 'active' | 'ended';
  peerPhone: string | null;
  peerName: string | null;
  direction: 'inbound' | 'outbound' | null;
  muted: boolean;
  /** Segundos desde que a chamada foi atendida. */
  seconds: number;
  error: string | null;
  routing: CallRoutingDecision | null;
}

const IDLE: CallUiState = {
  phase: 'idle',
  peerPhone: null,
  peerName: null,
  direction: null,
  muted: false,
  seconds: 0,
  error: null,
  routing: null,
};

export interface CallsApi {
  state: CallUiState;
  /** Há telefonia configurada e conectada neste painel. */
  available: boolean;
  dial: (phone: string, name?: string | null) => Promise<void>;
  accept: () => Promise<void>;
  reject: () => Promise<void>;
  hangup: () => Promise<void>;
  toggleMute: () => Promise<void>;
  dismiss: () => void;
}

/**
 * Telefonia do painel.
 *
 * O áudio nunca passa pelo nosso servidor: o SDK do Wavoip abre WebRTC direto
 * do navegador. Ao servidor cabem duas coisas que o navegador não pode decidir
 * sozinho — se esta pessoa deve ser incomodada por esta chamada, e o registro
 * do que aconteceu.
 */
export function useCalls(enabled: boolean): CallsApi {
  const [state, setState] = useState<CallUiState>(IDLE);
  const [available, setAvailable] = useState(false);

  const wavoipRef = useRef<Wavoip | null>(null);
  const offerRef = useRef<Offer | null>(null);
  const activeRef = useRef<CallActive | null>(null);
  const timerRef = useRef<number | null>(null);
  const providerCallId = useRef<string | null>(null);
  /** Marco do atendimento; base para a duração informada ao servidor. */
  const answeredAtRef = useRef<number | null>(null);

  const stopTimer = useCallback(() => {
    if (timerRef.current !== null) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const startTimer = useCallback(() => {
    stopTimer();
    const startedAt = Date.now();
    timerRef.current = window.setInterval(() => {
      setState((current) => ({ ...current, seconds: Math.floor((Date.now() - startedAt) / 1000) }));
    }, 1000);
  }, [stopTimer]);

  /** Envia ao servidor o que só o navegador sabe: quem atendeu e por quanto tempo. */
  const report = useCallback(
    (status: 'active' | 'ended' | 'rejected' | 'failed' | 'missed', phone: string, direction: 'inbound' | 'outbound', seconds = 0) => {
      void api
        .reportCall({
          peerPhone: phone,
          direction,
          status,
          providerCallId: providerCallId.current ?? undefined,
          durationSeconds: seconds,
        })
        .catch(() => {
          // Registro é secundário: nunca deve atrapalhar a ligação em curso.
        });
    },
    [],
  );

  const bindActive = useCallback(
    (call: CallActive, direction: 'inbound' | 'outbound', phone: string) => {
      activeRef.current = call;
      providerCallId.current = call.id;
      answeredAtRef.current = Date.now();
      startTimer();
      setState((current) => ({ ...current, phase: 'active', direction, seconds: 0, muted: false }));
      report('active', phone, direction);

      call.on('ended', () => {
        const answeredAt = answeredAtRef.current;
        const seconds = answeredAt ? Math.round((Date.now() - answeredAt) / 1000) : 0;

        stopTimer();
        activeRef.current = null;
        answeredAtRef.current = null;
        report('ended', phone, direction, seconds);
        setState((current) => ({ ...current, phase: 'ended', seconds }));
      });

      call.on('error', (err) => {
        setState((current) => ({ ...current, error: String(err) }));
      });
    },
    [report, startTimer, stopTimer],
  );

  // ─────────────────── conexão com o provedor ───────────────────

  useEffect(() => {
    if (!enabled) return;

    let cancelled = false;
    let instance: Wavoip | null = null;

    void (async () => {
      let tokens: string[] = [];
      try {
        tokens = (await api.callDevices()).tokens;
      } catch {
        return;
      }

      if (cancelled || tokens.length === 0) return;

      instance = new Wavoip({ tokens, language: 'pt-BR', platform: 'supporchat' });
      wavoipRef.current = instance;

      instance.on('offer', (offer) => {
        void handleOffer(offer);
      });

      /**
       * `available` acompanha o dispositivo, não o token.
       *
       * Ter token não significa que a telefonia funciona: o token pode estar
       * errado, o dispositivo pode estar fora do ar ou hibernando. Habilitar o
       * botão "Ligar" só por existir um token entrega um botão que falha —
       * pior que não mostrar botão nenhum.
       */
      const syncAvailability = () => {
        const ready = instance!.getDevices().some((device) => isReady(device.status));
        setAvailable(ready);
      };

      for (const device of instance.getDevices()) {
        device.onStatus(() => syncAvailability());
      }

      syncAvailability();
      // Dispositivo hibernando só volta se for acordado; sem isto a primeira
      // ligação do dia falharia.
      void instance.wakeUpDevices();
    })();

    /**
     * Uma oferta chega a todos os painéis ligados ao mesmo número — o provedor
     * não sabe quem é o dono da conversa. Por isso perguntamos ao servidor
     * antes de tocar; quem não é o responsável simplesmente ignora em silêncio,
     * sem rejeitar (rejeitar derrubaria a chamada para todo mundo).
     */
    async function handleOffer(offer: Offer): Promise<void> {
      const phone = offer.peer.phone;

      let routing: CallRoutingDecision;
      try {
        routing = await api.callRouting(phone);
      } catch {
        // Sem resposta do servidor, é melhor tocar do que perder a ligação.
        routing = {
          shouldRing: true,
          reason: 'no_conversation',
          conversationId: null,
          contactId: null,
          contactName: null,
          contactAvatarUpdatedAt: null,
          assignedToName: null,
        };
      }

      if (!routing.shouldRing) return;

      offerRef.current = offer;
      providerCallId.current = offer.id;

      setState({
        ...IDLE,
        phase: 'incoming',
        direction: 'inbound',
        peerPhone: phone,
        // O nome do nosso cadastro vem antes do que o WhatsApp mostra: é o que
        // o atendente reconhece.
        peerName: routing.contactName ?? offer.peer.displayName ?? null,
        routing,
      });

      notifyOperator(routing.contactName ?? phone);

      // Se outro atendente pegou (ou o cliente desistiu), a tela some sozinha.
      offer.on('acceptedElsewhere', () => setState(IDLE));
      offer.on('rejectedElsewhere', () => setState(IDLE));
      offer.on('unanswered', () => {
        report('missed', phone, 'inbound');
        setState(IDLE);
      });
      offer.on('ended', () => setState(IDLE));
    }

    return () => {
      cancelled = true;
      stopTimer();
      instance?.removeAllListeners('offer');
      wavoipRef.current = null;
      setAvailable(false);
    };
  }, [enabled, report, stopTimer]);

  // ─────────────────────────── ações ───────────────────────────

  const dial = useCallback(
    async (phone: string, name?: string | null) => {
      const wavoip = wavoipRef.current;
      if (!wavoip) {
        setState({ ...IDLE, error: 'Chamadas não estão configuradas para este número.' });
        return;
      }

      setState({ ...IDLE, phase: 'dialing', direction: 'outbound', peerPhone: phone, peerName: name ?? null });

      const { call, err } = await wavoip.startCall({ to: phone });

      if (err || !call) {
        setState((current) => ({
          ...current,
          phase: 'ended',
          error: err?.message ?? 'Não foi possível iniciar a chamada.',
        }));
        report('failed', phone, 'outbound');
        return;
      }

      providerCallId.current = call.id;

      call.on('peerAccept', (active) => bindActive(active, 'outbound', phone));
      call.on('peerReject', () => {
        report('rejected', phone, 'outbound');
        setState((current) => ({ ...current, phase: 'ended', error: 'O cliente recusou a chamada.' }));
      });
      call.on('unanswered', () => {
        report('missed', phone, 'outbound');
        setState((current) => ({ ...current, phase: 'ended', error: 'O cliente não atendeu.' }));
      });
      call.on('ended', () => {
        stopTimer();
        setState((current) => (current.phase === 'active' ? { ...current, phase: 'ended' } : current));
      });
    },
    [bindActive, report, stopTimer],
  );

  const accept = useCallback(async () => {
    const offer = offerRef.current;
    if (!offer) return;

    // accept() pede permissão de microfone: só funciona a partir de um clique.
    const { call, err } = await offer.accept();
    offerRef.current = null;

    if (err || !call) {
      setState((current) => ({ ...current, phase: 'ended', error: err ?? 'Falha ao atender.' }));
      return;
    }

    bindActive(call, 'inbound', offer.peer.phone);
  }, [bindActive]);

  const reject = useCallback(async () => {
    const offer = offerRef.current;
    offerRef.current = null;
    if (offer) {
      await offer.reject();
      report('rejected', offer.peer.phone, 'inbound');
    }
    setState(IDLE);
  }, [report]);

  const hangup = useCallback(async () => {
    const call = activeRef.current;
    stopTimer();
    if (call) {
      await call.end();
      activeRef.current = null;
    }
    setState((current) => ({ ...current, phase: 'ended' }));
  }, [stopTimer]);

  const toggleMute = useCallback(async () => {
    const call = activeRef.current;
    if (!call) return;
    const next = !state.muted;
    await (next ? call.mute() : call.unmute());
    setState((current) => ({ ...current, muted: next }));
  }, [state.muted]);

  const dismiss = useCallback(() => {
    stopTimer();
    setState(IDLE);
  }, [stopTimer]);

  return { state, available, dial, accept, reject, hangup, toggleMute, dismiss };
}

/** Estados em que o dispositivo do Wavoip aceita originar e receber chamada. */
function isReady(status: string): boolean {
  return status === 'open' || status === 'UP';
}

/**
 * Chamada só é útil se alguém perceber. A aba costuma estar em segundo plano,
 * então além do toque na tela pedimos a notificação do sistema. A permissão é
 * gerida em lib/notify, junto com a dos avisos de mensagem.
 */
function notifyOperator(who: string): void {
  notify({
    title: 'Chamada recebida',
    body: `${who} está ligando`,
    tag: 'supporchat-call',
    // Chamada não pode sumir sozinha da tela: ela expira em segundos.
    requireInteraction: true,
  });
}
